/**
 * Capability configuration for {@link SpecificationViewer} when rendered in panel mode
 * (`isPanelMode`). `isPanelMode` controls only the compact panel *layout* (BTItemHeader instead of
 * the full-page BdsContentHeader, no outer split chrome); this config controls which secondary
 * features and actions the panel surfaces. Splitting the two lets multiple panel consumers share the
 * same layout while showing different action sets:
 *
 * - the linked-entities drawer (SpecificationsPanelLayout) — the lightweight default,
 * - the job-level AG Grid panel (#280748) — the full set,
 * - the company catalog panel (#282024) — a reduced set,
 * - a future fully read-only panel — no editing at all.
 *
 * Prefer the exported presets over hand-building a config at each call site.
 */
export interface ISpecificationPanelConfig {
    /** Render the Published/Draft status badge next to the title. */
    status: boolean;
    /** Surface the Version History button + its in-panel split/takeover. */
    versionHistory: boolean;
    /** Surface the Comments button + its in-panel split. */
    comments: boolean;
    /** Allow entering edit mode / editing the spec (Edit icon, attachments, Save/Cancel). */
    canEdit: boolean;
    /**
     * Surface the per-spec Share (copy link / QR) dropdown. Panel mode only — book view always
     * shows it regardless of this flag, since `bookView` reuses LINKED_DRAWER_PANEL_CONFIG below.
     */
    share: boolean;
}

/**
 * Default profile — the linked-entities drawer (SpecificationsDrawer / SpecificationsPanelLayout).
 * Lightweight: title but no status/version-history/comments, and still editable. Used when a panel
 * consumer omits `panelConfig`. The drawer relies on the viewer's own title (rather than its own
 * separate header row) so it shares the same single-row header as the job/catalog panels.
 *
 * `share: false` is the one capability where the drawer diverges from the book view (#283821):
 * per-spec sharing doesn't fit the panel workflow — the user is there to reference a linked spec in
 * the context of another record, not to distribute it externally. `bookView` reuses this same
 * config object but is unaffected, because the share flag is only consulted in panel mode.
 */
export const LINKED_DRAWER_PANEL_CONFIG: ISpecificationPanelConfig = {
    status: false,
    versionHistory: false,
    comments: false,
    canEdit: true,
    share: false,
};

/** Job-level AG Grid detail panel (#280748) — the full action set. */
export const JOB_PANEL_CONFIG: ISpecificationPanelConfig = {
    status: true,
    versionHistory: true,
    comments: true,
    canEdit: true,
    share: true,
};

/**
 * Company catalog detail panel (#282024) — reduced set: title shown, but no status badge, version
 * history, or comments. Editing is allowed (Edit icon + Save/Cancel). Publish / Print /
 * Move-to-Draft / Viewing-Permissions / Save-to-Catalog are already hidden for the catalog because
 * it renders with `isGeneralJob`.
 */
export const CATALOG_PANEL_CONFIG: ISpecificationPanelConfig = {
    status: false,
    versionHistory: false,
    comments: false,
    canEdit: true,
    share: false,
};

/**
 * Fully read-only detail panel — title only, no status/version-history/comments, and NO editing at
 * all (no Edit icon, no attachment changes). Used when previewing a spec you don't own/manage in the
 * current context, e.g. the job-level "Add from catalog" import modal, where a builder browses
 * company catalog specs to import but must not edit the catalog copy.
 */
export const READONLY_PANEL_CONFIG: ISpecificationPanelConfig = {
    status: false,
    versionHistory: false,
    comments: false,
    canEdit: false,
    share: false,
};

/**
 * Explicit mode for SpecificationViewer consumers, replacing hand-picked `isPanelMode`/`panelConfig`
 * combinations at call sites.
 * - `bookView` — the full-page book view (SpecificationBook), not panel mode at all.
 * - `linkedDrawer` — the linked-entities drawer (SpecificationsPanelLayout), panel mode with the
 *   lightweight default config (same capabilities as `bookView`, different layout).
 * - `job` — the job-level AG Grid panel (#280748), the full action set.
 * - `catalogManage` — the company catalog panel (#282024), managing catalog specs directly.
 * - `catalogImport` — browsing catalog specs to import into a job (read-only preview).
 */
export type SpecificationPanelMode =
    | "bookView"
    | "linkedDrawer"
    | "job"
    | "catalogManage"
    | "catalogImport";

/** Whether a given panel mode operates on general-job (catalog) specs rather than job-level specs. */
export const GENERAL_JOB_PANEL_MODES: ReadonlySet<SpecificationPanelMode> = new Set([
    "catalogManage",
    "catalogImport",
]);

/** Panel modes that render SpecificationViewer's compact panel layout (BTItemHeader, no outer split
 * chrome). `bookView` is the only mode that renders the full-page layout instead. */
export const IN_PANEL_MODES: ReadonlySet<SpecificationPanelMode> = new Set([
    "linkedDrawer",
    "job",
    "catalogManage",
    "catalogImport",
]);

export const SPECIFICATION_PANEL_CONFIG_BY_MODE: Record<
    SpecificationPanelMode,
    ISpecificationPanelConfig
> = {
    bookView: LINKED_DRAWER_PANEL_CONFIG,
    linkedDrawer: LINKED_DRAWER_PANEL_CONFIG,
    job: JOB_PANEL_CONFIG,
    catalogManage: CATALOG_PANEL_CONFIG,
    catalogImport: READONLY_PANEL_CONFIG,
};
